import React, { useState, useEffect } from 'react';
import axios from 'axios';

import { CONTINUATION_TYPES } from '../../../constants';

const axiosClient = axios.create({
	withCredentials: true,
	baseURL: '/api/v2/form-section-advanced-settings',
});

/**
 * Fetches the advanced settings for a form section.
 *
 * @param {Object}  formSection         The form section for which to fetch the settings.
 * @param {Object}  options             Set of additional options for the hook.
 * @param {Boolean} options.isPreview  Flag indicating whether the embedded form is a preview.
 *
 * @returns {Object}  result          Data related to the form section advanced settings.
 * @returns {Array}   result.data     The list of form section advanced settings.
 * @returns {Boolean} result.loading  Flag indicating whether the settings data is being loaded.
 */
const useAdvancedSettings = (formSection, options = {}) => {

	const [advancedSettings, setAdvancedSettings] = useState({
		data: [],
		loading: true,
		error: false,
	});

	useEffect(() => {

		if (!formSection) {
			return;
		}

		// Use pre-loaded settings if this is a preview in the FormEditor component
		if (options.isPreview) {

			setAdvancedSettings({
				data: formSection.advanced_settings,
				loading: false,
				error: false,
			});

			return;

		}

		// No need to fetch advanced settings if section does not have an advanced
		// continuation type
		if (formSection.continuation_type !== CONTINUATION_TYPES.PRODUCT_TICKETS &&
			formSection.continuation_type !== CONTINUATION_TYPES.PRODUCT_TICKET_GROUPS) {

			setAdvancedSettings((prev) => ({
				...prev,
				data: [],
				loading: false,
				error: false,
			}));

			return;

		}

		// Otherwise fetch the advanced settings for the section

		// Set loading state if needed
		setAdvancedSettings((prev) => ({
			...prev,
			loading: true,
			error: false,
		}));

		// Fetch the advanced settings for the section
		const params = {
			object_type: formSection.continuation_type,
			form_section_id: formSection.id,
			form_id: formSection.form_id,
			organization_id: formSection.organization_id,
		};

		axiosClient.get('/', { params }).then((response) => {

			setAdvancedSettings({
				data: response.data,
				loading: false,
				error: false,
			});

		}).catch((axiosError) => {

			setAdvancedSettings({
				data: [],
				loading: false,
				error: true,
			});

			console.error(axiosError);

		});

	}, [formSection, options.isPreview]);

	return advancedSettings;

};

export default useAdvancedSettings;
